import React, {PureComponent} from "react";
import {connect, DispatchProp} from "react-redux";
import {cssHelper} from "../../utils/cssHelper";
import {BonusType, getBonusGroup} from "../../../store/player/types";
import {toCssClassName} from "../../utils/toCssClassName";
import {IApplicationState} from "../../../store";
import {applyHint} from "../../../store/hints/actions";
import {BottomPanel} from "../bottom-panel/BottomPanel";
import styles from "./hint-panel.module.css";
import iconStyles from "../../styles/icons.module.css";
import bottomPanelStyles from "../bottom-panel/bottom-panel.module.css";
import {AnimationSettingsProp} from "../../../store/settings/types";
import getFontSize from "../../utils/getFontSize";
import {TweenLite, Animation} from "gsap";
import i18n from "../../i18n";

interface HintPanelConnectedProps {
    readonly availableCoins: number;
    readonly availableBonuses: Record<number, number>;
    readonly hintCost: Record<number, number>;
    readonly activeHints: Record<string, BonusType[]>;
    readonly appliedHints: Record<string, BonusType[]>;
    readonly isExitFound: boolean;
    readonly allTilesAreRevealed: boolean;
    readonly hasUnusedQuestions: boolean;
    readonly hasThreeOrMoreAnswers: boolean;
    readonly hasQuestionStats: boolean;
}

interface HintPanelProps extends HintPanelConnectedProps {
    readonly delay?: number;
    readonly animate: boolean;
    readonly id: string;
    readonly caption?: string;
    readonly hints: BonusType[];
}

class HintPanel extends PureComponent<HintPanelProps & AnimationSettingsProp & DispatchProp> {
    private readonly coins: React.RefObject<HTMLSpanElement>;
    private readonly activeTweens: Animation[] = [];

    constructor(props: HintPanelProps & AnimationSettingsProp & DispatchProp, context: any) {
        super(props, context);

        this.coins = React.createRef();
    }

    render() {
        const {caption, delay, availableCoins, animate, animation} = this.props;

        const header = caption
            ? (
                <>
                    <div>{caption}</div>
                    <div className={styles.coins}>
                        <i className={cssHelper(iconStyles.icon, iconStyles.coins).toString()}/>
                        <span ref={this.coins}>{availableCoins}</span>
                    </div>
                </>
            )
            : undefined;

        const hints = this.renderHints();

        return (
            <BottomPanel delay={delay} caption={header} animation={{...animation, enabled: animation.enabled && animate}}>
                {hints}
            </BottomPanel>
        );
    }

    componentDidUpdate(prevProps: Readonly<HintPanelProps & AnimationSettingsProp & DispatchProp>, prevState: Readonly<{}>, snapshot?: any): void {
        const {animation} = this.props;

        if (!this.coins.current || !animation.enabled)
            return;

        if (prevProps.availableCoins !== this.props.availableCoins) {
            const coinsCounter = { coins: prevProps.availableCoins };
            const tween = TweenLite.fromTo(coinsCounter, 1, {
                coins: prevProps.availableCoins,
            }, {
                coins: this.props.availableCoins,
                roundProps: "coins",
                onUpdate: () => {
                    this.coins.current && (this.coins.current.innerHTML = coinsCounter.coins + "");
                }
            });

            this.activeTweens.push(tween);
        }
    }

    componentWillUnmount(): void {
        for (let animation of this.activeTweens) {
            animation.kill();
        }
    }

    private renderHints() {
        const {
            id,
            hints,
            hintCost,
            availableBonuses,
            availableCoins} = this.props;

        const activeHints = this.props.activeHints[id] || [];
        const appliedHints = this.props.appliedHints[id] || [];

        const maxStringLength = Math.max(...hints
            .flatMap(hint => i18n(`bonuses.${getBonusGroup(hint)}.${BonusType[hint].toLowerCase()}.captionname` as any).split(/\n/))
            .map(titlePart => titlePart.length));

        const diff = (hints.length - 3);
        const fontSizeDiff = diff * 2;
        const sizeDiff = diff * 2;
        const paddingDiff = diff * 15;

        const buttonStyle = {
            backgroundSize: getFontSize(maxStringLength, 18 - sizeDiff, 20 - sizeDiff, 8, 15).toFixed(2) + "%",
        };

        const captionStyle = {
            fontSize: getFontSize(maxStringLength, 16 - fontSizeDiff, 22 - fontSizeDiff, 8, 15).toFixed(1) + "px",
            paddingLeft: getFontSize(maxStringLength, 58 - paddingDiff, 68 - paddingDiff, 8, 15).toFixed(1) + "px"
        };

        return hints.map((hint, index) => {
            const isActive = activeHints.includes(hint);
            const isApplied = appliedHints.includes(hint);
            const className = cssHelper(bottomPanelStyles.button, styles.hint, styles[`hint-${hints.length}`])
                .add(styles.hint_active, isActive)
                .add(styles[`${toCssClassName(BonusType[hint])}`]);

            const cost = hintCost[hint];
            const title = i18n(`bonuses.${getBonusGroup(hint)}.${BonusType[hint].toLowerCase()}.captionname` as any);
            const isDisabled = isActive
                || isApplied
                || (!availableBonuses[hint] && cost > availableCoins)
                || !this.hintCanBeApplied(hint);

            return (
                <button key={index} className={className.toString()} onClick={this.getOnClickHandler(id, hint)} style={buttonStyle} disabled={isDisabled}>
                    <span className={styles.caption} style={captionStyle}>{title}</span>
                    <small className={styles.cost}>
                        {
                            availableBonuses[hint] > 0
                                ? (<>×{availableBonuses[hint]}</>)
                                : (<><i className={cssHelper(iconStyles.icon, iconStyles.coins_white).toString()}/>{cost}</>)
                        }
                    </small>
                </button>
            );
        });
    }

    private getOnClickHandler(id: string, hint: BonusType) {
        return () => {
            this.props.dispatch(applyHint.request({id, hint}));
        };
    }

    private hintCanBeApplied(hint: BonusType) {
        const hasAppliedSibling = () => {
            return (this.props.appliedHints[this.props.id] && this.props.appliedHints[this.props.id].length)
                || (this.props.activeHints[this.props.id] && this.props.activeHints[this.props.id].length);
        };

        switch (hint) {
            case BonusType.RevealTile:
            case BonusType.RevealMap:
                return !this.props.allTilesAreRevealed;

            case BonusType.ShowExit:
                return !this.props.isExitFound;

            case BonusType.SkipQuestion:
                return this.props.hasUnusedQuestions && !hasAppliedSibling();

            case BonusType.MajorityVote:
                return this.props.hasQuestionStats && !hasAppliedSibling();

            case BonusType.FiftyFifty:
            case BonusType.DoubleChance:
                return this.props.hasThreeOrMoreAnswers && !hasAppliedSibling();
        }

        return true;
    }
}

export default connect(({ player, game, questions, hints, settings }: IApplicationState)  => {
    const levelProgress = game.levelMapProgress[game.levelInProgress];
    const question = game.questions[questions.currentQuestionId];

    return {
        availableCoins: player.coins,
        availableBonuses: player.bonuses,
        hintCost: settings.gameBalance.hintCost,
        activeHints: hints.activeHints,
        appliedHints: hints.appliedHints,
        isExitFound: levelProgress && levelProgress.isExitFound,
        allTilesAreRevealed: levelProgress && levelProgress.allTilesAreRevealed,
        hasUnusedQuestions: !!game.unusedQuestions.length,
        hasThreeOrMoreAnswers: question && question.answers.length > 2,
        hasQuestionStats: question && question.answerStats && question.answerStats.totalAnswerCount > 0 && question.answerStats.stats.length > 0,
        animation: settings.animation,
    };
})(HintPanel);